import { lazy } from 'react';
import type { LazyExoticComponent, ComponentType } from 'react';
import {
  Activity,
  Headset,
  BarChart3,
  Users,
  UserCheck,
  Clock,
  Video,
  Eye,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type WidgetCategory = 'activity' | 'support' | 'admin';

export interface WidgetProps {
  width: number;
  height: number;
  isEditMode: boolean;
}

export interface WidgetDef {
  id: string;
  icon: LucideIcon;
  labelKey: string;
  component: LazyExoticComponent<ComponentType<WidgetProps>>;
  defaultSize: { w: number; h: number };
  minSize: { w: number; h: number };
  category: WidgetCategory;
  perm?: 'canCreateReports' | `tab:${string}`;
}

export const WIDGET_DEFS: WidgetDef[] = [
  {
    id: 'activity',
    icon: Activity,
    labelKey: 'home.widgets.names.activity',
    component: lazy(() => import('./items/ActivityWidget').then((m) => ({ default: m.ActivityWidget }))),
    defaultSize: { w: 5, h: 1 },
    minSize: { w: 4, h: 1 },
    category: 'activity',
  },
  {
    id: 'support',
    icon: Headset,
    labelKey: 'home.widgets.names.support',
    component: lazy(() => import('./items/SupportWidget').then((m) => ({ default: m.SupportWidget }))),
    defaultSize: { w: 5, h: 1 },
    minSize: { w: 4, h: 1 },
    category: 'support',
  },
  // ── Admin Widgets ──
  {
    id: 'adminTotalUsers',
    icon: Users,
    labelKey: 'home.widgets.names.adminTotalUsers',
    component: lazy(() => import('./items/AdminQuickStatsWidget').then((m) => ({ default: m.AdminTotalUsersWidget }))),
    defaultSize: { w: 3, h: 1 },
    minSize: { w: 3, h: 1 },
    category: 'admin',
    perm: 'tab:dashboard',
  },
  {
    id: 'adminActiveUsers',
    icon: UserCheck,
    labelKey: 'home.widgets.names.adminActiveUsers',
    component: lazy(() => import('./items/AdminQuickStatsWidget').then((m) => ({ default: m.AdminActiveUsersWidget }))),
    defaultSize: { w: 3, h: 1 },
    minSize: { w: 3, h: 1 },
    category: 'admin',
    perm: 'tab:dashboard',
  },
  {
    id: 'adminPendingUsers',
    icon: Clock,
    labelKey: 'home.widgets.names.adminPendingUsers',
    component: lazy(() => import('./items/AdminQuickStatsWidget').then((m) => ({ default: m.AdminPendingUsersWidget }))),
    defaultSize: { w: 3, h: 1 },
    minSize: { w: 3, h: 1 },
    category: 'admin',
    perm: 'tab:dashboard',
  },
  {
    id: 'adminTotalVideos',
    icon: Video,
    labelKey: 'home.widgets.names.adminTotalVideos',
    component: lazy(() => import('./items/AdminQuickStatsWidget').then((m) => ({ default: m.AdminTotalVideosWidget }))),
    defaultSize: { w: 3, h: 1 },
    minSize: { w: 3, h: 1 },
    category: 'admin',
    perm: 'tab:dashboard',
  },
  {
    id: 'adminTotalViews',
    icon: Eye,
    labelKey: 'home.widgets.names.adminTotalViews',
    component: lazy(() => import('./items/AdminQuickStatsWidget').then((m) => ({ default: m.AdminTotalViewsWidget }))),
    defaultSize: { w: 3, h: 1 },
    minSize: { w: 3, h: 1 },
    category: 'admin',
    perm: 'tab:dashboard',
  },
  {
    id: 'adminActivityBreakdown',
    icon: BarChart3,
    labelKey: 'home.widgets.names.adminActivityBreakdown',
    component: lazy(() => import('./items/AdminActivityBreakdownWidget').then((m) => ({ default: m.AdminActivityBreakdownWidget }))),
    defaultSize: { w: 5, h: 2 },
    minSize: { w: 4, h: 1 },
    category: 'admin',
    perm: 'tab:dashboard',
  },
  {
    id: 'adminRecentActivity',
    icon: Activity,
    labelKey: 'home.widgets.names.adminRecentActivity',
    component: lazy(() => import('./items/AdminRecentActivityWidget').then((m) => ({ default: m.AdminRecentActivityWidget }))),
    defaultSize: { w: 5, h: 1 },
    minSize: { w: 4, h: 1 },
    category: 'admin',
    perm: 'tab:dashboard',
  },
  {
    id: 'adminQuickActions',
    icon: Zap,
    labelKey: 'home.widgets.names.adminQuickActions',
    component: lazy(() => import('./items/AdminQuickActionsWidget').then((m) => ({ default: m.AdminQuickActionsWidget }))),
    defaultSize: { w: 12, h: 1 },
    minSize: { w: 4, h: 1 },
    category: 'admin',
    perm: 'tab:dashboard',
  },
];

export const WIDGET_MAP = new Map(WIDGET_DEFS.map((w) => [w.id, w]));

export const DEFAULT_WIDGET_IDS = [
  'activity',
  'support',
];

interface PermissionContext {
  canCreateReports: boolean;
  isAdmin: boolean;
  hasPermission: (perm: string) => boolean;
}

export function filterWidgetsByPermissions(widgets: WidgetDef[], perms: PermissionContext): WidgetDef[] {
  return widgets.filter((w) => {
    if (!w.perm) return true;
    // Admins can see all widgets
    if (perms.isAdmin) return true;
    if (w.perm === 'canCreateReports') return perms.canCreateReports;
    if (w.perm.startsWith('tab:')) return perms.hasPermission(w.perm);
    return true;
  });
}
